import { ProjectFileTypeEnum } from '@cyberlabrs/helpers';
import { ProjectFileWithPresignedUrlDto } from '../../../commonTypes/project.types';
import { MixerLoader } from '../../Common';
import { formatToAmPm, formatToFullMonthDayYear } from '../../../util/date/dateFormater';
import { useMemo, useState } from 'react';

interface ProjectFilesTableProps {
    tableHeadCellData: TableHeadCellData[];
    tableData: ProjectFileWithPresignedUrlDto[];
    isDataReady: boolean;
}

type TableHeadCellData = {
    label: string;
    className?: string;
};

export default function ProjectFilesTable(props: ProjectFilesTableProps) {
    const { tableHeadCellData, tableData, isDataReady } = props;

    type SortKey = 'fileType' | 'name' | 'date' | 'time';
    type SortDirection = 'asc' | 'desc';

    const [sortKey, setSortKey] = useState<SortKey>('date');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

    const sortedData = useMemo(() => {
        const data = [...tableData];

        data.sort((a, b) => {
            let valA: any;
            let valB: any;

            switch (sortKey) {
                case 'fileType':
                    valA = a.fileType;
                    valB = b.fileType;
                    break;
                case 'name':
                    valA = a.name.toLowerCase();
                    valB = b.name.toLowerCase();
                    break;
                case 'date':
                    valA = new Date(a.date).getTime();
                    valB = new Date(b.date).getTime();
                    break;
                case 'time':
                    valA = a.time;
                    valB = b.time;
                    break;
            }

            if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
            if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });

        return data;
    }, [tableData, sortKey, sortDirection]);

    const handleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(key);
            setSortDirection('asc');
        }
    };

    const getSortKey = (label: string): SortKey | null => {
        switch (label) {
            case 'File':
                return 'fileType';
            case 'Name':
                return 'name';
            case 'Date':
                return 'date';
            case 'Time':
                return 'time';
            default:
                return null;
        }
    };

    const getIcon = (fileType: ProjectFileTypeEnum) => {
        switch (fileType) {
            case ProjectFileTypeEnum.PDF:
                return <img src="/svg/common/pdf.svg" alt="pdf"></img>;
            case ProjectFileTypeEnum.IMG:
                return <img src="/svg/common/img.svg" alt="img"></img>;
            case ProjectFileTypeEnum.XLS:
                return <img src="/svg/common/excel.svg" alt="xls"></img>;
            default:
                return <img src="/svg/common/pdf.svg" alt="pdf"></img>;
        }
    };

    const handleDownload = (file: ProjectFileWithPresignedUrlDto) => {
        if (!file.presignedUrl) return;

        const link = document.createElement('a');
        link.href = file.presignedUrl;
        link.download = file.name;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return isDataReady ? (
        tableData.length > 0 ? (
            <div className="project-files-table">
                <div className="project-files-table__header">
                    {tableHeadCellData.map(item => {
                        const key = getSortKey(item.label);
                        const isActive = key === sortKey;

                        return (
                            <div
                                key={item.label}
                                className="project-files-table__header-cell"
                                onClick={() => key && handleSort(key)}
                                style={{ cursor: key ? 'pointer' : 'default' }}
                            >
                                <span className="project-files-table__header-text">{item.label}</span>

                                {key && (
                                    <img
                                        src={isActive ? '/svg/common/sort-arrow-blue.svg' : '/svg/common/sort-arrow.svg'}
                                        alt="sort-arrow"
                                        style={{
                                            transform: isActive && sortDirection === 'desc' ? 'rotate(180deg)' : 'none',
                                        }}
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>

                {sortedData.map(row => (
                    <div key={row.id} className="project-files-table__row">
                        <span className="project-files-table__icon">{getIcon(row.fileType)}</span>
                        <span className="project-files-table__item">{row.name}</span>
                        <span className="project-files-table__item">{formatToFullMonthDayYear(row.date)}</span>
                        <span className="project-files-table__item">{formatToAmPm(row.time)}</span>
                        <button className="project-files-table__action" onClick={() => handleDownload(row)} aria-label="Download file">
                            <img src="/svg/common/download-blue.svg" alt="download" />
                        </button>
                    </div>
                ))}
            </div>
        ) : (
            <div className="project-files-table__no-results">
                <img className="project-files-table__no-results__image" src="/svg/project-files-no-results.svg" alt="no-results" />
                <h2 className="project-files-table__no-results__title">Your Storage Is a Blank Canvas</h2>
                <p className="project-files-table__no-results__paragraph">
                    Start filling it with project files, exports, reports or anything your team wants to keep within reach.
                </p>
            </div>
        )
    ) : (
        <MixerLoader position="centerOnParent" />
    );
}
